<template>
  <teleport to="body">
    <div class="absolute inset-0 z-20 overflow-hidden pointer-events-none">
      <transition name="intro-popup-backdrop" appear>
        <div
          class="absolute inset-0 z-10 duration-300 bg-black will-change-[opacity] transition-al"
          :class="[
            showIntroPopup
              ? [
                  'bg-opacity-50',
                  'pointer-events-auto',
                  'supports-bg-filter:bg-saturate-blur-full',
                ]
              : [
                  'bg-opacity-0',
                  'pointer-events-none',
                  'supports-bg-filter:bg-saturate-blur-0',
                ],
          ]"
          @click="close"
        />
      </transition>
      <transition
        name="intro-popup"
        appear
        @after-enter="() => lightboxInitialize()"
        @after-leave="() => lightboxSafeDestroy()"
      >
        <div
          v-if="showIntroPopup"
          class="intro-popup absolute top-1/2 left-1/2 z-20 -translate-x-1/2 -translate-y-1/2 bg-orange-500 border-20 border-solid border-orange-500 rounded-2xl shadow-xl pointer-events-auto"
        >
          <div class="bg-white w-full h-full flex flex-col py-8 rounded-md">
            <h1
              class="px-8 pb-4 text-2xl leading-8 font-medium text-orange-500"
            >
              Mededelingenbord
            </h1>

            <div
              class="flex flex-col justify-start grow p-8 pt-4 divide-y divide-orange-500 divide-solid overflow-y-auto"
            >
              <div
                v-for="(contentElement, index) in introContent"
                :key="contentElement"
                class="py-5"
                :class="[
                  {
                    grow: !!contentElement.flexGrow,
                    'pb-0': index === introContent.length - 1,
                    'pt-0': index === 0,
                  },
                ]"
                v-html="contentElement._rendered"
              ></div>
            </div>
          </div>

          <div class="absolute inset-x-0 top-0 flex justify-between w-full">
            <button
              class="flex items-center justify-center w-8 h-8 -ml-4 -mt-4 bg-white text-orange-500 hover:text-orange-600 border-4 border-orange-500 rounded-full hover:border-orange-600 cursor-pointer focus:outline-none active:outline-none transition-colors duration-300"
              @click="close"
            >
              <svg
                width="137"
                height="136"
                viewBox="0 0 137 136"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                class="w-4 h-4 stroke-current"
              >
                <line
                  x1="22"
                  y1="113.924"
                  x2="113.924"
                  y2="22"
                  stroke="currentColor"
                  stroke-width="30"
                  stroke-linecap="round"
                />
                <line
                  x1="23.2132"
                  y1="22"
                  x2="115.137"
                  y2="113.924"
                  stroke="currentColor"
                  stroke-width="30"
                  stroke-linecap="round"
                />
              </svg>
            </button>
            <div>
              <input
                :id="checkboxId"
                v-model="introPopupDismissedPersistable"
                type="checkbox"
                class="sr-only checkbox-dismiss"
              />
              <label
                :for="checkboxId"
                class="flex items-center pt-3 pr-3 text-orange-500 hover:text-orange-600 cursor-pointer select-none transition-colors duration-300"
              >
                Dit niet meer laten zien
              </label>
            </div>
          </div>
        </div>
      </transition>
    </div>
  </teleport>
</template>

<script>
import { computed, ref, Teleport, watchEffect } from "vue";
import { useStore } from "vuex";

import lightboxInitialize from "../content/lightbox-initialize";
import lightboxSafeDestroy from "../content/lightbox-safe-destroy";

let uuid = 0;

export default {
  name: "IntroPopup",
  components: {
    Teleport,
  },

  setup() {
    uuid += 1;

    // Store
    const store = useStore();
    const container = computed(() => store.state.container);
    const containerLoaded = computed(
      () => !!Object.keys(container.value).length,
    );
    const introContent = computed(() => container.value.introContent);

    // Util
    const checkboxId = `intro-popup--${uuid}__meta__control--dismiss`;
    const readStorage = (key) => JSON.parse(window.localStorage.getItem(key));
    const writeStorage = (key, value) =>
      window.localStorage.setItem(key, JSON.stringify(value));
    // Note: `container.value.intro*` depends on `container` having finished loading!
    // Therefore, we only call `readStorage`, using `storageKey`, after
    // having watched `container.value.introContent` change from undefined to an array.
    const storageKey = computed(
      () => `intro[${container.value.introStartDate}].dismissed`,
    );

    // State
    const introPopupClosed = ref(false);
    // Handle for the localStorage dismissed state at load time.
    const introPopupPreviouslyDismissed = ref(undefined);
    // Handle for the runtime dismissed state, persistable via computed setter.
    const introPopupDismissed = ref(undefined);

    // "Do not show this again" checkbox model.
    // Writes to local storage on change, for persistence.
    const introPopupDismissedPersistable = computed({
      get() {
        return introPopupDismissed.value;
      },
      set(value) {
        introPopupDismissed.value = value;
        writeStorage(storageKey.value, value);
      },
    });

    watchEffect(() => {
      // Wait for `introContent`, `introStartDate` and `introEndDate` to appear.
      // Requires store `container` to have finished loading.
      if (!containerLoaded.value) {
        return;
      }
      // Read previous popup dismissal from local storage.
      introPopupPreviouslyDismissed.value =
        readStorage(storageKey.value) || false;
      // If the popup has been dismissed and the dismissal persisted before,
      // then definitely do not show the popup now.
      introPopupDismissed.value = introPopupPreviouslyDismissed.value;
    });

    // Decide to show or not to show the popup - depending on everything defined above.
    const showIntroPopup = computed(() => {
      // Getter dependency => Wait for `container` to load...
      if (!containerLoaded.value) {
        return false;
      }
      // Do not show if there is no intro content (possibly even after loading).
      if (!introContent.value.length) {
        return false;
      }
      // Do not show if intro period has not yet started.
      // Note `introStartDate` is *not* a required field in TCA.
      if (
        container.value.introStartDate &&
        new Date(Date.now()) < new Date(container.value.introStartDate)
      ) {
        return false;
      }
      // Do not show if intro period has passed.
      // (`introEndDate` is *not* a required field in TCA.)
      if (
        container.value.introEndDate &&
        new Date(Date.now()) > new Date(container.value.introEndDate)
      ) {
        return false;
      }
      // Do not show if user has dismissed the intro popup.
      if (introPopupPreviouslyDismissed.value) {
        return false;
      }
      // Do not show if the user has closed the intro popup (possibly without dismissing).
      return !introPopupClosed.value;
    });

    return {
      showIntroPopup,
      introContent,
      checkboxId,
      introPopupDismissedPersistable,
      close: () => (introPopupClosed.value = true),
      lightboxInitialize,
      lightboxSafeDestroy,
    };
  },
};
</script>

<style scoped lang="css">
/** Vue `<transition>` intro-popup-backdrop **/
.intro-popup-backdrop-enter-active {
  transition: all 0.4s ease-out;
}

.intro-popup-backdrop-leave-active {
  transition: all 0.4s ease-in; /* easeInBack */
}

.intro-popup-backdrop-enter-from,
.intro-popup-backdrop-leave-to {
  @apply opacity-0;
}

/** Vue `<transition>` intro-popup **/
.intro-popup-enter-active {
  transition: all 0.8s cubic-bezier(0.175, 0.885, 0.32, 1.275); /* easeOutBack */
}

.intro-popup-leave-active {
  transition: all 0.8s cubic-bezier(0.6, -0.28, 0.735, 0.045); /* easeInBack */
}

.intro-popup-enter-from,
.intro-popup-leave-to {
  transform: translate3d(-50%, -200%, 0);
}
</style>
